import React, {useEffect, useRef} from 'react'
import {Button} from 'react-daisyui'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faCircleInfo} from '@fortawesome/free-solid-svg-icons'
import LoadingSpinner, {LoadingSpinnerSize} from '../common/LoadingSpinner'
import {
  ModifyQuoteResponse,
  recapMoneyBlock,
  recapNote,
  recapRows,
  RecapPriceLine
} from './modifyReservationHelpers'

export interface ApplyErrorNotice {
  readonly message: string
  readonly tone: 'warning' | 'error'
}

interface ChangeRecapProps {
  currentCheckin?: string
  currentCheckout?: string
  arrival: Date
  departure: Date
  quote: ModifyQuoteResponse
  applyError: ApplyErrorNotice | null
  isBusy: boolean
  onBackToDates: () => void
  onConfirm: () => void
}

const priceLineClassFor = (
  kind: RecapPriceLine['kind'],
  isFooter: boolean
): string => {
  switch (kind) {
    case 'total':
      return isFooter
        ? 'font-semibold text-base'
        : 'font-medium border-t border-base-300 mt-1 pt-1'
    case 'increase':
      return 'text-amber-600 font-semibold'
    case 'decrease':
      return 'text-green-600 font-semibold'
    default:
      return 'text-sm'
  }
}

const PriceRow: React.FC<{line: RecapPriceLine; isFooter?: boolean}> = ({
  line,
  isFooter = false
}) => (
  <div
    className={`flex justify-between py-0.5 ${priceLineClassFor(
      line.kind,
      isFooter
    )}`}
  >
    <span>{line.label}</span>
    <span>{line.value}</span>
  </div>
)

const ChangeRecap: React.FC<ChangeRecapProps> = ({
  currentCheckin,
  currentCheckout,
  arrival,
  departure,
  quote,
  applyError,
  isBusy,
  onBackToDates,
  onConfirm
}) => {
  const headingRef = useRef<HTMLHeadingElement>(null)
  useEffect(() => headingRef.current?.focus(), [])

  const rows = recapRows(currentCheckin, currentCheckout, arrival, departure)
  const moneyBlock = recapMoneyBlock(
    quote,
    currentCheckin,
    currentCheckout,
    arrival,
    departure
  )

  return (
    // Capped and internally scrollable: the modal-box is overflow-visible
    // (for the date-picker on the dates step), so a tall recap must
    // contain itself — heading and actions stay put, the middle scrolls
    <div className="flex max-h-[75vh] flex-col">
      <div className="flex-shrink-0">
        <h1
          id="modify-reservation-title"
          ref={headingRef}
          tabIndex={-1}
          className="font-bold text-2xl mb-2 outline-none"
          role="heading"
          aria-level={1}
        >
          Review your change
        </h1>
        <p className="mb-4">Nothing changes until you confirm.</p>

        {applyError && (
          <div
            className={
              applyError.tone === 'warning'
                ? 'bg-base-200 border-l-4 border-warning rounded p-3 text-sm mb-4'
                : 'text-red-500 mb-4'
            }
            role="alert"
            aria-live="assertive"
          >
            {applyError.message}
          </div>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pr-1">
        <div className="mb-4">
          {rows.map(row => (
            <div
              key={row.label}
              className="flex justify-between items-baseline gap-3 py-2 border-b border-base-200 last:border-b-0"
            >
              <span className="text-sm">{row.label}</span>
              <span className="text-right font-medium">
                {row.changed ? (
                  <>
                    {row.previous !== '—' && (
                      <>
                        <span className="line-through font-normal opacity-60 mr-2">
                          {row.previous}
                        </span>
                        <span aria-hidden="true" className="mr-2">
                          →
                        </span>
                      </>
                    )}
                    {row.next}
                  </>
                ) : (
                  <>
                    {row.next}
                    <span className="text-xs font-normal opacity-60 ml-2">
                      unchanged
                    </span>
                  </>
                )}
              </span>
            </div>
          ))}
        </div>

        <div className="bg-base-200 rounded-lg p-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 md:gap-x-8">
            {moneyBlock.groups.map((group, index) => (
              <div
                key={group.title}
                className={
                  index > 0
                    ? 'mt-3 pt-3 border-t border-base-300 md:mt-0 md:pt-0 md:border-t-0 md:border-l md:pl-8'
                    : ''
                }
              >
                <p className="text-xs font-medium uppercase tracking-wide opacity-60 mb-1">
                  {group.title}
                </p>
                {group.lines.map(line => (
                  <PriceRow key={line.label} line={line} />
                ))}
              </div>
            ))}
          </div>
          {moneyBlock.footer.length > 0 && (
            <div className="mt-3 pt-3 border-t-2 border-base-300">
              {moneyBlock.footer.map(line => (
                <PriceRow key={line.label} line={line} isFooter />
              ))}
            </div>
          )}
        </div>

        <div className="flex gap-2 items-start bg-base-200 border-l-4 border-accent rounded p-3 text-sm mb-4">
          <FontAwesomeIcon icon={faCircleInfo} className="mt-0.5" aria-hidden />
          <span>{recapNote(quote)}</span>
        </div>
      </div>

      <div className="flex-shrink-0">
        {isBusy && (
          <div
            className="flex flex-row justify-center items-center py-2 gap-2"
            role="status"
            aria-live="polite"
          >
            <LoadingSpinner size={LoadingSpinnerSize.Large} />
            <span>Updating…</span>
          </div>
        )}

        <div className="flex justify-end p-2 gap-2">
          <Button
            className="btn-md btn-outline btn-secondary"
            disabled={isBusy}
            onClick={onBackToDates}
            aria-label="Go back and change the dates"
          >
            back to dates
          </Button>
          <Button
            className="btn btn-md btn-secondary"
            disabled={isBusy}
            onClick={onConfirm}
            aria-label="Confirm the date change at the shown price"
          >
            confirm change
          </Button>
        </div>
      </div>
    </div>
  )
}

export default ChangeRecap
